import React, { FunctionComponent, useMemo } from 'react';
import classnames from 'classnames';

import { SavingsCalculator as SavingsCalculatorComponent, SavingsCalculatorProps } from '@savebetter/components';

import { useDatasources } from '../context/DataSourceContext';

import { PageSectionBlok, PageSectionProps } from './PageSection';
import { Section } from './Section';

interface Blok extends PageSectionBlok, SavingsCalculatorProps {}

interface SavingsCalculatorComponentProps extends PageSectionProps {
  blok: Blok;
}

const defaultFormValues: SavingsCalculatorProps = {
  initialDeposit: 50000,
  monthlyContribution: 100,
  maturity: 2,
  apy: 5,
  title: 'Savings Interest Calculator: See How Much Interest You Could Earn',
  description:
    'Try our savings interest calculator to see how much interest you could be earning ' +
    'with Raisin’s savings vs. other banks. ' +
    'Our savings interest calculator is designed with transparency in mind to help you ' +
    'achieve your financial goals.'
};

export const SavingsCalculator: FunctionComponent<SavingsCalculatorComponentProps> = ({ blok, sectionClassName }) => {
  const {
    title,
    description,
    footerText,
    hasPaddingTop,
    hasPaddingBottom = false,
    hasBackground = false,
    hasBorder = false
  } = blok;

  const { contextDatasources: datasources } = useDatasources();

  const initialValues = useMemo(() => {
    if (!datasources || !datasources['savings_calculator_default_values']) {
      return { ...defaultFormValues, title, description, footerText };
    }

    const initialFormValues = datasources['savings_calculator_default_values'].edges.reduce(
      (initialValues, { node }) => ({
        ...initialValues,
        [node.name]: node.value
      }),
      {} as Partial<SavingsCalculatorProps>
    );

    return { ...defaultFormValues, ...initialFormValues, title, description, footerText } as SavingsCalculatorProps;
  }, [datasources, title, description, footerText]);

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={classnames(sectionClassName, 'my-10')}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      <SavingsCalculatorComponent {...initialValues} />
    </Section>
  );
};
